import { FC, useCallback, useMemo } from "react";

import { UserEntityModalType, useFullUserProfileContext } from "Bands/contexts";
import { useFetchingContactDetails } from "Bands/utils";
import { copy } from "shared/assets";
import { Phone, Plus } from "shared/assets/icons";
import { Button, Card, ContactTeaser, Loader, Message } from "shared/components";
import { Spacer } from "shared/layout";
import { GridContainer, Icon } from "shared/styles";
import { ITheme } from "shared/types";

const phoneCopy = copy.userPhoneNumber;

export const PhoneNumbers: FC = () => {
  const { phoneNumbers, setShowCreateModal, setShowEditModal, setShowDeleteModal, getItemActions } =
    useFullUserProfileContext();

  const getActions = useCallback(
    (id: string) =>
      getItemActions({
        handleEdit: () =>
          setShowEditModal({
            id: id,
            type: UserEntityModalType.phoneNumber,
          }),
        handleDelete: () =>
          setShowDeleteModal({
            id: id,
            type: UserEntityModalType.phoneNumber,
          }),
      }),
    [setShowDeleteModal, setShowEditModal, getItemActions]
  );

  const { fetchingPhoneNumbers } = useFetchingContactDetails();

  const content = useMemo(() => {
    if (fetchingPhoneNumbers) {
      return <Loader />;
    }

    if (!phoneNumbers?.length) {
      return <Message message={phoneCopy.messages.empty} />;
    }

    return (
      <GridContainer>
        {phoneNumbers?.map(phoneNumber => (
          <ContactTeaser
            id={phoneNumber.id}
            key={phoneNumber.id}
            phone={phoneNumber.phoneNumber}
            actions={getActions(phoneNumber.id)}
            note={phoneNumber.note}
          ></ContactTeaser>
        ))}
      </GridContainer>
    );
  }, [fetchingPhoneNumbers, phoneNumbers, getActions]);

  return (
    <Card icon={Phone} title={phoneCopy.title}>
      <Spacer size={phoneNumbers?.length ? "xl" : "s"}>
        {content}
        <Button
          onClick={() => setShowCreateModal(UserEntityModalType.phoneNumber)}
          data-testid="add_phone_button"
          theme={ITheme.secondary}
        >
          <Icon as={Plus} />
          <span>{phoneCopy.buttons.create}</span>
        </Button>
      </Spacer>
    </Card>
  );
};
